import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAllCollectionsByUserId, addCollection, deleteCollectionById } from '../services/apiService';
import EditCollectionModal from './EditCollectionModal';
import CollectionSelectModal from './CollectionSelectModal';
import ConfirmDialog from './ConfirmDialog';
import './MyKuraTab.css';

const MyKuraTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openedCollectionId, setOpenedCollectionId] = useState(null);
  const [editingCollection, setEditingCollection] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingCollectionId, setDeletingCollectionId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    fetchCollections();
    
    // Check if we're currently viewing a collection
    const pathMatch = location.pathname.match(/\/collection\/([^/]+)/);
    if (pathMatch) {
      setOpenedCollectionId(pathMatch[1]);
    }
  }, [location.pathname]);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllCollectionsByUserId();
      if (response.success) {
        setCollections(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching collections:', err);
      setError('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const ordinal = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
    return `${month} ${day}${ordinal}, ${year}`;
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const handleCollectionClick = (collectionId, e) => {
    // Prevent navigation if clicking the edit icon
    if (e && e.target.closest('.my-kura-edit-icon')) {
      return;
    }
    navigate(`/collection/${collectionId}`);
  };

  const handleEditClick = (collection, e) => {
    e.stopPropagation();
    setEditingCollection(collection);
  };

  const handleEditClose = () => {
    setEditingCollection(null);
  };

  const handleEditUpdate = () => {
    // Refresh collections after update
    fetchCollections();
  };

  const handleDeleteClick = (collectionId, e) => {
    e.stopPropagation();
    setPendingDeleteId(collectionId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteId) return;
    
    try {
      setDeletingCollectionId(pendingDeleteId);
      await deleteCollectionById(pendingDeleteId);
      fetchCollections();
      // If the deleted collection was opened, navigate back
      if (openedCollectionId === pendingDeleteId) {
        navigate('/');
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
      alert('Failed to delete collection');
    } finally {
      setDeletingCollectionId(null);
      setShowDeleteConfirm(false);
      setPendingDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setPendingDeleteId(null);
  };

  const handleCreateCollection = async (collectionData) => {
    try {
      await addCollection(collectionData);
      setShowCreateModal(false);
      fetchCollections();
    } catch (error) {
      console.error('Error creating collection:', error);
      alert('Failed to create collection');
    }
  };

  if (loading) {
    return (
      <div className="my-kura-loading">
        <div className="my-kura-spinner"></div>
        <p>Loading collections...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-kura-error">
        <p>{error}</p>
      </div>
    );
  }

  if (collections.length === 0) {
    return (
      <div className="my-kura-empty">
        <p>No collections yet. Create your first collection by saving an image!</p>
      </div>
    );
  }

  return (
    <div className="my-kura-container">
      <div className="my-kura-header">
        <button
          className="my-kura-create-btn"
          onClick={() => setShowCreateModal(true)}
        >
          + Create Collection
        </button>
      </div>
      <div className="my-kura-grid">
        {collections.map((collection) => {
          const firstImage = collection.images && collection.images.length > 0 ? collection.images[0].url : null;
          const isOpened = openedCollectionId === collection.id;

          return (
            <div
              key={collection.id}
              className={`my-kura-card ${isOpened ? 'opened' : ''}`}
              onClick={(e) => handleCollectionClick(collection.id, e)}
            >
              {isOpened && (
                <div className="my-kura-opened-tag">Opened</div>
              )}
              
              <div className="my-kura-card-image">
                {firstImage ? (
                  <img src={firstImage} alt={collection.name} />
                ) : (
                  <div className="my-kura-card-placeholder">
                    <span>No images</span>
                  </div>
                )}
              </div>
              
              <div className="my-kura-card-content">
                <div className="my-kura-card-name-wrapper">
                  <h3 className="my-kura-card-name">{collection.name}</h3>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="my-kura-edit-icon"
                      onClick={(e) => handleEditClick(collection, e)}
                      title="Edit collection"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button
                      className="my-kura-delete-icon"
                      onClick={(e) => handleDeleteClick(collection.id, e)}
                      title="Delete collection"
                      disabled={deletingCollectionId === collection.id}
                      style={{ opacity: deletingCollectionId === collection.id ? 0.5 : 1 }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="my-kura-card-dates">
                  <span>Created {formatDate(collection.createdAt)}</span>
                  <span> | </span>
                  <span>Modified {formatTimeAgo(collection.updatedAt)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editingCollection && (
        <EditCollectionModal
          isOpen={!!editingCollection}
          onClose={handleEditClose}
          collection={editingCollection}
          onUpdate={handleEditUpdate}
        />
      )}

      {showCreateModal && (
        <CollectionSelectModal
          isOpen={showCreateModal}
          mode="create"
          onClose={() => setShowCreateModal(false)}
          imageId={null}
          onCreateCollection={handleCreateCollection}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Delete Collection"
        message="Are you sure you want to delete this collection? This action cannot be undone."
      />
    </div>
  );
};

export default MyKuraTab;

